// «Έλεγχος παραδοχών» - a read-only review of the study inputs.
//
// It only READS the project's settings and reports what looks unusual; it
// changes nothing, feeds nothing into the run and blocks nothing. Every rule
// here comes from a real study that went wrong because an input was not
// noticed: a battery priced 65,000 € instead of 650,000 € (13 €/kWh), an
// augmentation cost copied from a 1 MW project into a 25 MW one, a capture
// ratio measured on another battery, and the spread compression that decides
// the NPV of a merchant battery without the user knowing what it means.

import {
  dispatchForesightBasisFor,
  foresightBatteryMismatch,
  normalizeMerchantRevenueProjection,
} from "./merchantRevenueProjection";
import { parseOverrideEur } from "./equipmentCostOverride";
import { normalizeBtm, isBtmSettlement } from "./btm";
import { agreedPower, isVirtualNetBilling, voltageLevels } from "./virtualNetBilling";

// ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026 art. 12α: the virtual net billing contract
// (Σ.Ε.Τ.Σ.) runs for 20 years.
export const VNB_CONTRACT_YEARS = 20;

// Battery hardware (catalog price x count, or the override) per kWh of
// installed energy. Deliberately wide: only a price an order of magnitude off
// is flagged, never a cheap or expensive quote.
export const BATTERY_PRICE_LOW_EUR_KWH = 50;
// The upper bound depends on the size: a home battery of a few kWh costs
// 400-900 €/kWh retail, a utility system well under 300 €/kWh.
export const BATTERY_PRICE_HIGH_EUR_KWH = 500;
export const BATTERY_PRICE_HIGH_SMALL_EUR_KWH = 1200; // below 30 kWh
export const BATTERY_PRICE_HIGH_MEDIUM_EUR_KWH = 800; // 30-500 kWh
export const batteryPriceHighEurKwh = (energyKwh) =>
  energyKwh < 30 ? BATTERY_PRICE_HIGH_SMALL_EUR_KWH
    : energyKwh < 500 ? BATTERY_PRICE_HIGH_MEDIUM_EUR_KWH
      : BATTERY_PRICE_HIGH_EUR_KWH;
// A one-off augmentation, per kWh of installed energy. A typical top-up
// restores 10-25% of the capacity at about 140 €/kWh, i.e. 15-35 €/kWh.
export const AUGMENTATION_LOW_EUR_KWH = 5;
export const AUGMENTATION_HIGH_EUR_KWH = 150;

const num = (value) => {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const fmt = (value, digits = 0) =>
  Number(value).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
// kWh of a small battery keep a decimal (2.6, not 3).
const fmtKwh = (value) => fmt(value, value < 100 && !Number.isInteger(value) ? 1 : 0);

/** The battery the study runs, as the Economics page reads it. */
export function studyBattery(cfg) {
  const systemType = String(cfg?.systemType || "Hybrid").trim().toLowerCase();
  if (systemType === "standalone pv") return null;
  const selected = Array.isArray(cfg?.bess?.selected) ? cfg.bess.selected : [];
  const name = String(cfg?.bess?.activeName || selected[0]?.name || "");
  const entry = selected.find((b) => b?.name === name);
  if (!entry) return null;
  const count = Math.max(1, Number(entry.count) || 1);
  const powerKw = (Number(entry.p_max_d) || 0) * count;
  const energyKwh = (Number(entry.energy) || 0) * count;
  if (!(energyKwh > 0)) return null;
  const override = parseOverrideEur(cfg?.feasibility?.economics?.equipmentCostOverride?.battery);
  return { name, count, powerKw, energyKwh, priceOverrideEur: override };
}

/**
 * The findings, most important first. Each is
 * { id, severity: "warning" | "info", title, why, where }.
 * batteryPriceEur: the battery hardware total (catalog unit price x count, or
 * the override), or null when it is not known.
 */
export function assumptionChecks(cfg, { batteryPriceEur = null, isGreek = true } = {}) {
  const t = (el, en) => (isGreek ? el : en);
  const findings = [];
  const checked = [];
  const merchant = String(cfg?.settlementType || "") === "Arbitrage";
  const battery = studyBattery(cfg);
  const inv = cfg?.feasibility?.economics?.investmentCosts || {};
  // Simplified costing prices the systems turnkey (€/kWp, €/kWh): the battery
  // price is that rate x kWh unless a price is entered for the battery.
  const simplified = String(inv.costingMode || "").trim().toLowerCase() === "simplified";
  const simpleWhere = t("Οικονομικές παραδοχές → Επενδυτικό Κόστος → Απλοποιημένη κοστολόγηση.",
    "Economics → Investment Costs → Simplified costing.");

  if (simplified) {
    checked.push(t("απλοποιημένη κοστολόγηση", "simplified costing"));
    const systemType = String(cfg?.systemType || "Hybrid").trim().toLowerCase();
    const missing = [];
    if (systemType !== "standalone bess" && !(num(inv.simplePvEurPerKwp) > 0)) missing.push(t("Φ/Β (€/kWp)", "PV (€/kWp)"));
    if (battery && battery.priceOverrideEur == null && !(num(inv.simpleBatteryEurPerKwh) > 0)) missing.push(t("μπαταρία (€/kWh)", "battery (€/kWh)"));
    if (missing.length) {
      findings.push({
        id: "simplified_missing",
        severity: "warning",
        title: t(`Λείπει τιμή: ${missing.join(", ")}`, `Missing price: ${missing.join(", ")}`),
        why: t("Με απλοποιημένη κοστολόγηση το κόστος κάθε συστήματος είναι η τιμή ανά kWp ή kWh επί το μέγεθός του· χωρίς τιμή το σύστημα μπαίνει στη μελέτη χωρίς κόστος.",
          "Under simplified costing each system costs its price per kWp or kWh times its size; without a price the system enters the study at no cost."),
        where: simpleWhere,
      });
    }
  }

  if (battery) {
    checked.push(t("τιμή μπαταρίας", "battery price"));
    const simplePrice = simplified && num(inv.simpleBatteryEurPerKwh) > 0
      ? num(inv.simpleBatteryEurPerKwh) * battery.energyKwh : null;
    const price = battery.priceOverrideEur ?? (simplified ? simplePrice : batteryPriceEur);
    if (price != null && price > 0) {
      const perKwh = price / battery.energyKwh;
      if (perKwh < BATTERY_PRICE_LOW_EUR_KWH || perKwh > batteryPriceHighEurKwh(battery.energyKwh)) {
        const low = perKwh < BATTERY_PRICE_LOW_EUR_KWH;
        findings.push({
          id: "battery_price",
          severity: "warning",
          title: t(
            `Τιμή μπαταρίας ${fmt(perKwh)} €/kWh`,
            `Battery price ${fmt(perKwh)} €/kWh`),
          why: t(
            `${fmt(price)} € για ${fmtKwh(battery.energyKwh)} kWh (${battery.count} × ${battery.name}). ` +
              (low
                ? "Είναι πολύ κάτω από κάθε συνηθισμένη τιμή εξοπλισμού· συχνά λείπει ένα μηδενικό ή η τιμή είναι ανά μονάδα αντί για το σύνολο."
                : "Είναι πολύ πάνω από κάθε συνηθισμένη τιμή εξοπλισμού· ελέγξτε αν η τιμή είναι για όλες τις μονάδες μαζί."),
            `${fmt(price)} € for ${fmtKwh(battery.energyKwh)} kWh (${battery.count} × ${battery.name}). ` +
              (low
                ? "This is far below any usual equipment price; often a zero is missing or the price is per unit instead of the total."
                : "This is far above any usual equipment price; check whether the price covers all the units together.")),
          where: simplified && battery.priceOverrideEur == null ? simpleWhere : t(
            "Τιμή στον κατάλογο της μπαταρίας (σελίδα BESS), ή «Επεξεργασία» του εξοπλισμού στις Οικονομικές παραδοχές → Επενδυτικό Κόστος.",
            "The battery's catalog price (BESS page), or «Edit» on the equipment in Economics → Investment Costs."),
        });
      }
    }

    const mode = String(cfg?.batteryPerformance?.augmentationMode || "manual");
    const fin = cfg?.feasibility?.economics?.financialParams || {};
    const cost = num(fin.augmentationCostEur) || 0;
    const year = num(fin.augmentationYear) || 0;
    if (mode === "manual" && cost > 0 && year > 0) {
      checked.push(t("κόστος αναπλήρωσης χωρητικότητας", "augmentation cost"));
      const perKwh = cost / battery.energyKwh;
      if (perKwh < AUGMENTATION_LOW_EUR_KWH || perKwh > AUGMENTATION_HIGH_EUR_KWH) {
        findings.push({
          id: "augmentation",
          severity: "warning",
          title: t(
            `Αναπλήρωση χωρητικότητας ${fmt(perKwh, 1)} €/kWh εγκατεστημένης ενέργειας`,
            `Augmentation ${fmt(perKwh, 1)} €/kWh of installed energy`),
          why: t(
            `${fmt(cost)} € το έτος ${year} για μπαταρία ${fmtKwh(battery.energyKwh)} kWh. ` +
              "Μια συνηθισμένη αναπλήρωση αποκαθιστά 10-25% της χωρητικότητας, περίπου 15-35 €/kWh εγκατεστημένης ενέργειας. " +
              "Ένα τόσο διαφορετικό ποσό έχει συχνά μείνει από άλλο έργο με άλλο μέγεθος μπαταρίας.",
            `${fmt(cost)} € in year ${year} for a ${fmtKwh(battery.energyKwh)} kWh battery. ` +
              "A usual top-up restores 10-25% of the capacity, about 15-35 €/kWh of installed energy. " +
              "An amount this different is often left over from another project with another battery size."),
          where: t(
            "Σελίδα BESS → Απόδοση μπαταρίας → Αναπλήρωση χωρητικότητας μπαταρίας (εκεί φαίνεται και η εκτίμηση της πλατφόρμας).",
            "BESS page → Battery Performance → Battery Augmentation (the platform's own estimate is shown there)."),
        });
      }
    }
  }

  if (merchant) {
    const projection = normalizeMerchantRevenueProjection(cfg);
    const provenance = projection.dispatchForesightProvenance;
    const factor = projection.dispatchForesightFactor;
    const basis = dispatchForesightBasisFor({ factor, provenance });
    checked.push(t("συντελεστής προσαρμογής εσόδων", "dispatch-realism factor"));
    const otherBattery = battery && String(provenance?.basis || "") === "measured"
      ? foresightBatteryMismatch(provenance?.bessDescription, battery.powerKw, battery.energyKwh)
      : "";
    if (otherBattery) {
      const measuredOn = (String(provenance.bessDescription || "").match(/^\s*([\d.]+\s*MW\s*\/\s*[\d.]+\s*MWh)/) || [])[1] || "";
      findings.push({
        id: "foresight_other_battery",
        severity: "warning",
        title: t(
          `Ο συντελεστής ${fmt(Number(factor), 3)} μετρήθηκε σε άλλη μπαταρία`,
          `The factor ${fmt(Number(factor), 3)} was measured on another battery`),
        why: t(
          `Μετρήθηκε σε ${measuredOn}, ενώ η μπαταρία του έργου είναι ${otherBattery}. ` +
            "Η αναφορά το γράφει· για μελέτη προς δανειστή μετρήστε τη μπαταρία του έργου.",
          `It was measured on ${measuredOn}, while the project's battery is ${otherBattery}. ` +
            "The report says so; for a study going to a lender, measure the project's battery."),
        where: t(
          "Οικονομικές παραδοχές → Προβολή εσόδων αγοράς → «Μέτρηση για τη μπαταρία του έργου».",
          "Economics → Merchant Revenue Projection → «Measure this project's battery»."),
      });
    } else if (basis === "none") {
      findings.push({
        id: "foresight_none",
        severity: "info",
        title: t("Χωρίς μείωση εσόδων για ατελή πρόβλεψη τιμών (1.00)",
          "No haircut for imperfect price forecasts (1.00)"),
        why: t(
          "Η κατανομή της μπαταρίας βελτιστοποιείται με πλήρη γνώση των τιμών. Στην πράξη οι προσφορές γίνονται με πρόβλεψη και αποφέρουν λιγότερο, οπότε τα έσοδα αυτής της μελέτης είναι το ανώτατο όριο.",
          "The battery is dispatched with perfect price foresight. Real bids are made on forecasts and capture less, so this study's revenue is an upper bound."),
        where: t(
          "Οικονομικές παραδοχές → Προβολή εσόδων αγοράς → Βάση προσαρμογής εσόδων για ατελή πρόβλεψη τιμών.",
          "Economics → Merchant Revenue Projection → Dispatch realism (capture ratio) basis."),
      });
    }

    if (projection.mode === "forward_curve_redispatch") {
      checked.push(t("συμπίεση διαφορών τιμών", "spread compression"));
      const compression = num(projection.forwardCurve?.spreadCompressionPctYear) || 0;
      if (compression > 0) {
        const keep = (years) => fmt(100 * (1 - compression / 100) ** years);
        findings.push({
          id: "spread_compression",
          severity: "info",
          title: t(
            `Ετήσια συμπίεση διαφορών τιμών ${fmt(compression, 1)}%`,
            `Annual spread compression ${fmt(compression, 1)}%`),
          why: t(
            `Κάθε χρόνο η διαφορά ακριβών και φθηνών ωρών μικραίνει κατά ${fmt(compression, 1)}% (σωρευτικά): μένει περίπου ${keep(9)}% το έτος 10 και ${keep(19)}% το έτος 20. ` +
              "Είναι συνήθως η παραδοχή που επηρεάζει περισσότερο την ΚΠΑ μιας μπαταρίας· μια μονάδα πάνω ή κάτω μπορεί να αλλάξει το πρόσημο της ΚΠΑ. Τεκμηριώστε την τιμή.",
            `Each year the gap between expensive and cheap hours narrows by ${fmt(compression, 1)}% (chained): about ${keep(9)}% is left in year 10 and ${keep(19)}% in year 20. ` +
              "It is usually the assumption that moves a battery's NPV most; one point up or down can change its sign. Document the value."),
          where: t(
            "Οικονομικές παραδοχές → Προβολή εσόδων αγοράς → Ετήσια συμπίεση διαφορών τιμών εντός ημέρας (%).",
            "Economics → Merchant Revenue Projection → Annual Spread Compression (%)."),
        });
      } else if (battery) {
        findings.push({
          id: "spread_compression_zero",
          severity: "info",
          title: t("Χωρίς συμπίεση διαφορών τιμών (0%)", "No spread compression (0%)"),
          why: t(
            "Οι διαφορές τιμών μέσα στην ημέρα ακολουθούν μόνο το επίπεδο τιμών για όλη τη διάρκεια του έργου. Αν μπουν πολλές νέες μπαταρίες στην αγορά, οι διαφορές μικραίνουν και τα έσοδα της μελέτης είναι αισιόδοξα.",
            "Intraday spreads follow the price level only, for the whole project life. If many new batteries enter the market, spreads narrow and this study's revenue is optimistic."),
          where: t(
            "Οικονομικές παραδοχές → Προβολή εσόδων αγοράς → Ετήσια συμπίεση διαφορών τιμών εντός ημέρας (%).",
            "Economics → Merchant Revenue Projection → Annual Spread Compression (%)."),
        });
      }
    }

    checked.push(t("ΦΠΑ της εμπορικής πρότασης", "VAT in the commercial proposal"));
    const recoverable = num(cfg?.commercialOffer?.pricing?.main?.vat_recoverable_pct) || 0;
    if (recoverable <= 0) {
      findings.push({
        id: "offer_vat",
        severity: "info",
        title: t("Ο ΦΠΑ της εμπορικής πρότασης δεν ανακτάται (0%)", "The commercial proposal's VAT is not recovered (0%)"),
        why: t(
          "Η απόσβεση στην πρόταση υπολογίζεται τότε με το ποσό με ΦΠΑ. Μια επιχείρηση που συμψηφίζει τον ΦΠΑ βλέπει μεγαλύτερη απόσβεση από την πραγματική. Η μελέτη δεν επηρεάζεται.",
          "The proposal's payback is then computed on the VAT-inclusive amount. A business that recovers VAT sees a longer payback than the real one. The study is not affected."),
        where: t(
          "Στοιχεία Μελέτης → στοιχεία προσφοράς → Ανακτήσιμο ποσοστό ΦΠΑ (%).",
          "Study Details → offer details → Recoverable VAT share (%)."),
      });
    }
  }

  // Virtual net billing: what the study assumes about the contract and the grid.
  if (isBtmSettlement(cfg?.settlementType) && isVirtualNetBilling(normalizeBtm(cfg))) {
    checked.push(t("διάρκεια σύμβασης εικονικού συμψηφισμού", "virtual net billing contract term"));
    const years = num(cfg?.feasibility?.settlementPeriod);
    if (years != null && years > VNB_CONTRACT_YEARS) {
      findings.push({
        id: "vnb_contract_term",
        severity: "warning",
        title: t(`Διάρκεια μελέτης ${fmt(years)} έτη, σύμβαση ${VNB_CONTRACT_YEARS} έτη`,
          `Study horizon ${fmt(years)} years, contract ${VNB_CONTRACT_YEARS} years`),
        why: t(
          `Η σύμβαση εικονικού συμψηφισμού (Σ.Ε.Τ.Σ.) διαρκεί ${VNB_CONTRACT_YEARS} έτη (ΚΥΑ άρθρο 12α). Η μελέτη υπολογίζει και τα έτη ${VNB_CONTRACT_YEARS + 1}–${fmt(years)} με τον ίδιο συμψηφισμό, ενώ η ΚΥΑ δεν ορίζει τι ισχύει μετά τη λήξη. Η ΚΠΑ, ο IRR και η αποπληρωμή περιλαμβάνουν έτσι όφελος που δεν καλύπτεται από τη σύμβαση.`,
          `The virtual net billing contract (Σ.Ε.Τ.Σ.) runs for ${VNB_CONTRACT_YEARS} years (ΚΥΑ art. 12α). The study values years ${VNB_CONTRACT_YEARS + 1}–${fmt(years)} with the same netting, while the ΚΥΑ does not say what applies after it ends. NPV, IRR and payback therefore include a benefit the contract does not cover.`),
        where: t(`Σελίδα «Προσομοίωση & Αναφορές» → «Διάρκεια έργου (έτη)»: ${VNB_CONTRACT_YEARS} έτη για να μείνει η μελέτη μέσα στη σύμβαση.`,
          `Simulation & Reports page → «Project lifetime (years)»: ${VNB_CONTRACT_YEARS} years keeps the study within the contract.`),
      });
    }
    checked.push(t("επίπεδο τάσης σταθμού και παροχών", "voltage level of station and supplies"));
    const voltage = voltageLevels(cfg?.btm?.virtualNetBilling);
    const levelName = (level) => ({ LV: t("ΧΤ", "LV"), MV: t("ΜΤ", "MV"), HV: t("ΥΤ", "HV") }[level] || level);
    const voltageWhere = t(
      "Σελίδα «Τεχνικά» → «Παροχές προς συμψηφισμό» → «Επίπεδο τάσης σταθμού» και το επίπεδο κάθε παροχής. Επιβεβαιώστε τα στις συμβάσεις σύνδεσης.",
      "Technical page → supplies to be netted → «Voltage level of the station» and each supply's level. Confirm them in the connection contracts.");
    if (voltage.differ) {
      const supplies = Array.isArray(cfg?.btm?.virtualNetBilling?.supplies) ? cfg.btm.virtualNetBilling.supplies : [];
      const list = voltage.levels.map((level, i) =>
        `${String(supplies[i]?.name || i + 1)} ${levelName(level)} ${voltage.factors[i].toFixed(3)}`).join(", ");
      findings.push({
        id: "vnb_voltage_level",
        severity: "info",
        title: t(`Παροχές σε άλλο επίπεδο τάσης από τον σταθμό (${levelName(voltage.station)})`,
          `Supplies at another voltage level than the station (${levelName(voltage.station)})`),
        why: t(
          `Η επιμερισθείσα ενέργεια κάθε τέτοιας παροχής πολλαπλασιάζεται με τον συντελεστή αναγωγής της ΚΥΑ (άρθρο 7), με ΣΠΑ ΜΤ ${fmt(voltage.mvPct, 2)}% και ΣΠΑ ΧΤ ${fmt(voltage.lvPct, 2)}%: ${list}. Ό,τι αφαιρεί ο συντελεστής από τον συμψηφισμό λογίζεται ως πλεονάζουσα ενέργεια. Οι ΣΠΑ αλλάζουν κάθε έτος.`,
          `Each such supply's allocated energy is multiplied by the reduction factor of the ΚΥΑ (art. 7), with ΣΠΑ ΜΤ ${fmt(voltage.mvPct, 2)}% and ΣΠΑ ΧΤ ${fmt(voltage.lvPct, 2)}%: ${list}. What the factor removes from netting counts as surplus. The ΣΠΑ change every year.`),
        where: voltageWhere,
      });
    } else if (voltage.station) {
      findings.push({
        id: "vnb_voltage_level",
        severity: "info",
        title: t(`Σταθμός και παροχές στο ίδιο επίπεδο τάσης (${levelName(voltage.station)})`,
          `Station and supplies at the same voltage level (${levelName(voltage.station)})`),
        why: t("Ο συντελεστής αναγωγής του άρθρου 7 της ΚΥΑ είναι 1 για όλες τις παροχές.",
          "The reduction factor of ΚΥΑ art. 7 is 1 for every supply."),
        where: voltageWhere,
      });
    } else findings.push({
      id: "vnb_voltage_level",
      severity: "info",
      title: t("Ίδιο επίπεδο τάσης για τον σταθμό και τις παροχές", "Same voltage level for the station and the supplies"),
      why: t(
        "Η μελέτη θεωρεί ότι ο σταθμός και όλες οι παροχές συνδέονται στο ίδιο επίπεδο τάσης (συντελεστής αναγωγής 1). Αν διαφέρουν, η ΚΥΑ (άρθρο 7) μειώνει την ενέργεια που επιμερίζεται σε κάθε παροχή κατά τις απώλειες του δικτύου, π.χ. από ΜΤ σε ΧΤ επί 1/(1+ΣΠΑ ΧΤ), οπότε η συμψηφισμένη ενέργεια και το όφελος είναι λίγο μικρότερα από αυτά της μελέτης.",
        "The study assumes the station and every supply connect at the same voltage level (reduction factor 1). If they differ, the ΚΥΑ (art. 7) reduces the energy allocated to each supply by the network losses, e.g. from MV to LV by 1/(1+ΣΠΑ LV), so the netted energy and the benefit are somewhat lower than in the study."),
      where: voltageWhere,
    });

    // Power limits (ΚΥΑ art. 4 par. 5β, art. 8 par. 2), from the supplies'
    // agreed power. Only checked when every supply has one.
    checked.push(t("όριο ισχύος σταθμού και έγχυσης", "station power and injection limits"));
    const agreed = agreedPower(cfg?.btm?.virtualNetBilling);
    const agreedWhere = t("Σελίδα «Τεχνικά» → «Παροχές προς συμψηφισμό» → «Συμφωνημένη ισχύς (kVA)» κάθε παροχής.",
      "Technical page → supplies to be netted → each supply's «Agreed power (kVA)».");
    if (!agreed.complete) {
      const missingEl = agreed.missing.length ? ` Λείπει η συμφωνημένη ισχύς: ${agreed.missing.join(", ")}.` : "";
      const missingEn = agreed.missing.length ? ` Agreed power missing: ${agreed.missing.join(", ")}.` : "";
      findings.push({
        id: "vnb_power_limit_unchecked",
        severity: "info",
        title: t("Το όριο ισχύος δεν ελέγχθηκε", "The power limit was not checked"),
        why: t(
          `Η εγκατεστημένη ισχύς του σταθμού δεν επιτρέπεται να ξεπερνά το άθροισμα της συμφωνημένης ισχύος των παροχών (ΚΥΑ άρθρο 4 παρ. 5), και το ίδιο ισχύει για την έγχυση Φ/Β και μπαταρίας μαζί (άρθρο 8 παρ. 2).${missingEl}`,
          `The station's installed power may not exceed the sum of the supplies' agreed power (ΚΥΑ art. 4 par. 5), and the same holds for the PV and battery injection together (art. 8 par. 2).${missingEn}`),
        where: agreedWhere,
      });
    } else {
      const total = agreed.totalKva;
      const totalText = `${fmt(total)} kVA`;
      const pvKwp = String(cfg?.systemType || "").trim().toLowerCase() === "standalone bess"
        ? null : num(cfg?.pvInstalledCap?.kw);
      const exportKw = num(cfg?.grid?.exportLimitKw);
      const within = [];
      if (pvKwp != null && pvKwp > 0) {
        if (pvKwp > total + 1e-9) {
          findings.push({
            id: "vnb_installed_power",
            severity: "warning",
            title: t(`Ισχύς Φ/Β ${fmt(pvKwp, 1)} kWp, συμφωνημένη ισχύς παροχών ${totalText}`,
              `PV power ${fmt(pvKwp, 1)} kWp, agreed power of the supplies ${totalText}`),
            why: t(
              "Η ΚΥΑ (άρθρο 4 παρ. 5β) επιτρέπει εγκατεστημένη ισχύ σταθμού έως το 100% του αθροίσματος της συμφωνημένης ισχύος των παροχών. Ο ΔΕΔΔΗΕ ελέγχει την ισχύ των πλαισίων (kWp). Με αυτή την ισχύ ο σταθμός δεν μπορεί να ενταχθεί στον εικονικό συμψηφισμό· η μελέτη δεν το εμποδίζει.",
              "The ΚΥΑ (art. 4 par. 5β) allows an installed station power of up to 100% of the sum of the supplies' agreed power. ΔΕΔΔΗΕ checks the module power (kWp). At this power the station cannot join virtual net billing; the study does not prevent it."),
            where: t("«Ρύθμιση Φωτοβολταϊκού Συστήματος» → εγκατεστημένη ισχύς, ή η συμφωνημένη ισχύς των παροχών στη σελίδα «Τεχνικά».",
              "«PV System Configuration» → installed capacity, or the supplies' agreed power on the Technical page."),
          });
        } else within.push(t(`Φ/Β ${fmt(pvKwp, 1)} kWp`, `PV ${fmt(pvKwp, 1)} kWp`));
      }
      const exportWhere = t("Σελίδα «Σύνδεση δικτύου» → «Όριο έγχυσης (kW)»: το μικρότερο από τη συμφωνημένη ισχύ των παροχών και τη μέγιστη ισχύ παραγωγής της σύμβασης σύνδεσης.",
        "«Grid Connection» → «Export Limit (kW)»: the smaller of the supplies' agreed power and the maximum production power in the connection contract.");
      if (!(exportKw > 0)) {
        findings.push({
          id: "vnb_injection_limit",
          severity: "warning",
          title: t("Δεν έχει οριστεί όριο έγχυσης", "No injection limit is set"),
          why: t(
            `Η έγχυση Φ/Β και μπαταρίας μαζί δεν επιτρέπεται να ξεπερνά τη συμφωνημένη ισχύ, ${totalText}, ούτε τη μέγιστη ισχύ παραγωγής της σύμβασης σύνδεσης (ΚΥΑ άρθρο 8 παρ. 2). Χωρίς όριο, η μελέτη αφήνει τον σταθμό να εγχέει όλη την παραγωγή του.`,
            `The PV and battery injection together may not exceed the agreed power, ${totalText}, nor the maximum production power in the connection contract (ΚΥΑ art. 8 par. 2). Without a limit, the study lets the station inject all of its output.`),
          where: exportWhere,
        });
      } else if (exportKw > total + 1e-9) {
        findings.push({
          id: "vnb_injection_limit",
          severity: "warning",
          title: t(`Όριο έγχυσης ${fmt(exportKw)} kW, συμφωνημένη ισχύς παροχών ${totalText}`,
            `Injection limit ${fmt(exportKw)} kW, agreed power of the supplies ${totalText}`),
          why: t(
            `Η μελέτη αφήνει τον σταθμό να εγχέει έως ${fmt(exportKw)} kW κάθε ώρα, ενώ η ΚΥΑ (άρθρο 8 παρ. 2α) περιορίζει την έγχυση Φ/Β και μπαταρίας μαζί στη συμφωνημένη ισχύ, ${totalText}. Η συμψηφισμένη και η πλεονάζουσα ενέργεια μπορεί να είναι μεγαλύτερες από όσο επιτρέπεται.`,
            `The study lets the station inject up to ${fmt(exportKw)} kW every hour, while the ΚΥΑ (art. 8 par. 2α) caps the PV and battery injection together at the agreed power, ${totalText}. The netted and surplus energy may be higher than allowed.`),
          where: exportWhere,
        });
      } else within.push(t(`όριο έγχυσης ${fmt(exportKw)} kW`, `injection limit ${fmt(exportKw)} kW`));
      if (battery && battery.powerKw > 0) {
        if (battery.powerKw > total + 1e-9) {
          findings.push({
            id: "vnb_battery_power",
            severity: "warning",
            title: t(`Ισχύς μπαταρίας ${fmt(battery.powerKw)} kW, συμφωνημένη ισχύς παροχών ${totalText}`,
              `Battery power ${fmt(battery.powerKw)} kW, agreed power of the supplies ${totalText}`),
            why: t(
              "Με διακριτό αντιστροφέα, η ονομαστική ισχύς του αντιστροφέα της μπαταρίας (σε VA) δεν επιτρέπεται να ξεπερνά τη συμφωνημένη ισχύ (ΚΥΑ άρθρο 8 παρ. 2). Ένας μεγαλύτερος αντιστροφέας επιτρέπεται μόνο με πιστοποιημένο περιορισμό της ισχύος του.",
              "With a separate inverter, the battery inverter's rated power (in VA) may not exceed the agreed power (ΚΥΑ art. 8 par. 2). A larger inverter is allowed only with a certified limit on its power."),
            where: t("Σελίδα «BESS» → η μπαταρία και το πλήθος της, ή η συμφωνημένη ισχύς των παροχών.",
              "BESS page → the battery and its count, or the supplies' agreed power."),
          });
        } else within.push(t(`μπαταρία ${fmt(battery.powerKw)} kW`, `battery ${fmt(battery.powerKw)} kW`));
      }
      if (within.length) {
        const listed = within.join(", ");
        const listedCap = listed.charAt(0).toUpperCase() + listed.slice(1);
        const verbEl = within.length === 1 ? "δεν ξεπερνά" : "δεν ξεπερνούν";
        const verbEn = within.length === 1 ? "is within" : "are within";
        findings.push({
          id: "vnb_power_limit_ok",
          severity: "info",
          title: t(`Εντός της συμφωνημένης ισχύος των παροχών (${totalText})`,
            `Within the supplies' agreed power (${totalText})`),
          why: t(
            `${listedCap}: ${verbEl} το άθροισμα της συμφωνημένης ισχύος των παροχών (ΚΥΑ άρθρο 4 παρ. 5, άρθρο 8 παρ. 2). Αν μια παροχή συμμετέχει και σε άλλο σύστημα αυτοκατανάλωσης, το όριο ισχύει για όλα μαζί, ανά ποσοστό συμμετοχής (άρθρο 3 παρ. 5).`,
            `${listedCap}: ${verbEn} the sum of the supplies' agreed power (ΚΥΑ art. 4 par. 5, art. 8 par. 2). If a supply also takes part in another self-consumption system, the limit applies to all of them together, by participation share (art. 3 par. 5).`),
          where: agreedWhere,
        });
      }
    }
  }

  const order = { warning: 0, info: 1 };
  findings.sort((a, b) => order[a.severity] - order[b.severity]);
  return { findings, checked };
}
